import { Component, Input, OnInit } from '@angular/core';
import { ElementType } from 'src/app/_objects/element-type';
import { ReportSummary } from 'src/app/_objects/report-summary';
import { FromToDetail, TableColumn } from 'src/app/_objects/report-template';
import { MousePoint, Notifier, ReportTemplateDesignService } from 'src/app/_services/report-template-design.service';

@Component({
    selector: 'app-report-summary-container',
    templateUrl: './report-summary.component.html',
    styleUrls: ['./report-summary.component.scss'],
    standalone: false
})
export class ReportSummaryComponent implements OnInit {

  @Input() public pageKey: string;
  @Input() public summaryKey: string;
  @Input() public elementLocation: string;

  public reportSummary: ReportSummary;
  private notifier: Notifier;
  public resizeMarkerSize: number;
  public resizing: boolean;

  constructor(public designService: ReportTemplateDesignService) {
    this.resizeMarkerSize = 36;
    this.resizing = false;
  }

  public ngOnInit() {
    this.notifier = {
      componentName: 'report-summary.component',
      ids: { pageId: this.pageKey, elementId: this.summaryKey }
    };
    this.reportSummary = this.designService.getSummary(this.summaryKey);
    let elementNavigator = this.designService.navigatorElements
    .filter((x) => x.pageKey == this.pageKey)[0]
    .elementsDetails.filter((y) => y.location == this.elementLocation)[0]
      .elements;

     elementNavigator.filter(x=>x.elementName == ElementType.Summary).length > 0
      ? elementNavigator.filter(x=>x.elementName == ElementType.Summary)[0].elementData.push({
          id: this.summaryKey,
          page: this.pageKey,
          name: this.reportSummary.getTitle(),
        })
      : elementNavigator.push({
          elementName: ElementType.Summary,
          isVisible: false,
          elementData: [
            {
              id: this.summaryKey,
              page: this.pageKey,
              name: this.reportSummary.getTitle(),
            },
          ],
        });
  }

  public onClick(event: MouseEvent) {
    if (this.reportSummary.isSelectable) {
      this.designService.notifySelectElement(this.notifier, this.summaryKey, event.ctrlKey);
      event.stopPropagation();
    }
  }

  public onDragStart(event: DragEvent) {
    // console.log('ReportSummary onDragStart');
    event.dataTransfer.setData('text', this.summaryKey);
    event.dataTransfer.effectAllowed = 'move';

    const fromDetail: FromToDetail = {
      location: this.reportSummary.getLocation(),
      pageId: this.pageKey
    };
    const dragPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };

    this.designService.notifyBeginDrag(this.notifier, ElementType.Summary, fromDetail, dragPoint);
  }

  public onDragOver(event: DragEvent) {
    // console.log('ReportSummary onDragOver');
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    event.stopPropagation();
  }

  public onDrop(event: DragEvent) {
    // console.log('ReportSummary onDrop');
    event.preventDefault();
    const template  = {...this.designService.originTemplate};
    template.reportTemplateContent = JSON.stringify(this.designService.getTemplate());
    this.designService.templateForUndo.push(template);
    const dragElemId = event.dataTransfer.getData('text');

    const toDetail: FromToDetail = {
      location: this.reportSummary.getLocation(),
      pageId: this.pageKey
    };
    const dropPoint: MousePoint = {
      onElementX: this.reportSummary.getLeftPx() + event.offsetX,
      onElementY: this.reportSummary.getTopPx() + event.offsetY
    };

    this.designService.notifyDrop(this.notifier, dragElemId, toDetail, dropPoint);

    event.dataTransfer.clearData('text');
    event.stopPropagation();
  }

  public getResizeMarkerSize(): string {
    return this.resizeMarkerSize + 'px';
  }

  public getTitleResizeMarkerLeft(horizontal: string): string {
    switch (horizontal) {
      case 'left':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'center':
        return ((this.reportSummary.getTitleWidthPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'right':
        return (this.reportSummary.getTitleWidthPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public getTitleResizeMarkerTop(vertical: string): string {
    switch (vertical) {
      case 'top':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'middle':
        return ((this.reportSummary.getTitleHeightPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'bottom':
        return (this.reportSummary.getTitleHeightPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public getTableResizeMarkerLeft(horizontal: string): string {
    switch (horizontal) {
      case 'left':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'center':
        return ((this.reportSummary.getTableWidthPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'right':
        return (this.reportSummary.getTableWidthPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public getTableResizeMarkerTop(vertical: string): string {
    switch (vertical) {
      case 'top':
        return (this.resizeMarkerSize / 2 * -1) + 'px';
      case 'middle':
        return ((this.reportSummary.getTableHeightPx() / 2) - (this.resizeMarkerSize / 2)) + 'px';
      case 'bottom':
        return (this.reportSummary.getTableHeightPx() - (this.resizeMarkerSize / 2)) + 'px';
      default:
        return '';
    }
  }

  public onResizeMarkerDown(event: MouseEvent, vertical: string, horizontal: string) {
    event.preventDefault();
    this.resizing = true;
    const beginPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };
    this.designService.notifyBeginResize(this.notifier, ElementType.Text, horizontal, vertical, beginPoint);

    event.stopPropagation();
  }

  public onTitleResizing(event: MouseEvent) {
    event.preventDefault();
    if (this.resizing) {
      this.designService.notifyResize(this.notifier, { onElementX: event.offsetX, onElementY: event.offsetY });
    }
    event.stopPropagation();
  }

  public onTableResizing(event: MouseEvent) {
    this.reportSummary.tableResize = true
    event.preventDefault();
    if (this.resizing) {
      this.designService.notifyResize(this.notifier, { onElementX: event.offsetX, onElementY: event.offsetY });
    }
    event.stopPropagation();
  }

  public onResizeMarkerUp(event: MouseEvent) {
    event.preventDefault();
    this.resizing = false;
    this.reportSummary.tableResize = false
    event.stopPropagation();
  }

  public onResizeMarkerLeave(event: MouseEvent) {
    event.preventDefault();
    this.resizing = false;
    this.reportSummary.tableResize = false
    event.stopPropagation();
  }

  replaceLabel(value: string): string {
    return value.replace('objects.', '');
  }
}
